import { Component, Inject, Input, Pipe, PipeTransform } from '@angular/core';

import { BehaviorSubject, Subject, combineLatest, of } from 'rxjs';
import { map, mergeMap, startWith } from 'rxjs/operators';

import { safeForkJoin } from '../../../../technical-modules/rxjs-helpers/rxjs-helpers';

import { filterUniques }  from '../../../../shared/basic-data-management/has-id.interface';
import { JSR310ConversionService } from '../../../../shared/jsr310-localdates/services/jsr310-conversion.service'; 
import { LocalDate } from '../../../../shared/jsr310-localdates/types/local-date.type'; 
import { MandatoryMap } from '../../../../shared/helpers/mandatory-map.class';

import { IntervenantService } from '../../../humain/intervenant/services/intervenant.service';

import { IntervenantTLR } from '../entities/intervenant-tlr.entity';

import { ParcService }  from "../../../structure/parc/services/parc.service";
import { ParcTLR }  from "../../../structure/parc/entities/parc-tlr.entity";

import { PlanPreventionService } from '../../../document-securite/services/plan-prevention.service';
import { PlanPreventionTLR } from '../../../document-securite/entities/plan-prevention-tlr.entity';

import { EntrepriseService } from '../../../entreprises/services/entreprise.service';
import { EntrepriseTLR } from '../../../entreprises/entities/entreprise-tlr.entity';

import { DocumentPaService } from '../../../document-securite/services/document-pa-service';
import { IntervenantPaInfo } from "../../../document-securite/entities/intervenant-pa-info.entity";

import { TypeHabilitationService } from '../../../habilitations/type-habilitation/services/type-habilitation.service';
import { TypeHabilitationWithQualCodesDTO }  from '../../../habilitations/type-habilitation/entities/type-habilitation-with-qual-codes-dto.entity';

import { TypeQualificationService } from '../../../habilitations/type-qualification/services/type-qualification.service';
import { OuvrageTLR } from '../../../structure/ouvrage/entities/ouvrage-tlr.entity';
import { ExpirationStatus } from '../../../../app.constants';

export enum TypeDocument {
    GENERAL = "GENERAL",
    SPECIFIQUE = "SPECIFIQUE",
	PA = "PA"
}

class DisplayedTypeQual {
	id!: number;
	iconClass!: string;
	title!: string;
	text!: string;
}

class DisplayedTypeHab {
	nomType!: string;
	codeParc!: string | null;
	qualificationCodes!: string;
	actif!: boolean;
	dateFin!: Date | null;
	valid!: boolean;
	qualificationTypeId!: number;
}

export class DisplayedTypePdp {
	id!: number;
	iconClass!: string;
	iconTitle!: string;
	iconText!: string | null;
	sectionTitle!: string;
	dateLimit!: Date;
	type!: TypeDocument;
}

type OuvrageAndPdPs = {
	ouvrageId: number,
	pdps: PlanPreventionTLR[],
};

@Component({
	selector: 'intervenant-status',
	templateUrl: 'intervenant-status.html',
	styleUrls: ['intervenant-status.css'],
})
export class IntervenantStatusComponent {
	private intervenantId$	= new Subject<number>();
	private ouvragesIds$ = new Subject<number[]>();
	private parcId$ = new Subject<number | null>();
	extended$ = new BehaviorSubject<boolean>(true);
	shouldBlink$ = new BehaviorSubject<string>('');
	pdpIconsToShow$ = new Subject<TypeDocument[]>();
	isSolaire = false;
	readonly hauteurQualificationId = 4;
	
	// Donnees a lier sur la vue
	loaded = false;
	displayedTypesQualifications: DisplayedTypeQual[] = [
		{
			id: 2,
			iconClass: 'fa fa-bolt fa-2x',
			title: 'Electricité HTA',
			text: 'HTA'
		},
		{
			id: 3,
			iconClass: 'fa fa-bolt fa-2x',
			title: 'Electricité HTB',
			text: 'HTB'
		},
		{
			id: 1,
			iconClass: 'fa fa-bolt fa-2x',
			title: 'Electricité BT',
			text: 'BT'
		},
		{
			id: 4,
			iconClass: 'fa fa-arrows-v fa-2x',
			title: 'Hauteur',
			text: ''
		},
	];
	typesClassesQual!: string[];
	private now = new Date();
	private getOneMonthAgo = () => {
		let date = new Date();
		date.setMonth(this.now.getMonth() - 1);
		return date;
	}
	private getOneWeekAgo = () => {
		let date = new Date();
		date.setDate(this.now.getDate() - 7);
		return date;
	}
	private getTwoMonthAgo = () => {
		let date = new Date();
		date.setMonth(this.now.getMonth() - 2);
		return date;
	}
	displayedTypesPdps: DisplayedTypePdp[] = [
		{
			id: 0,
			iconClass: 'fa fa-folder-open fa-2x',
			iconTitle: 'Plan de prévention générique',
			iconText: null,
			sectionTitle: 'PDP Généraux',
			dateLimit: this.getOneMonthAgo(),
			type: TypeDocument.GENERAL
		},
		{
			id: 1,
			iconClass: 'fa fa-folder fa-2x',
			iconTitle: 'Plan de prévention spécifique',
			iconText: null,
			sectionTitle: 'PDP Spécifiques',
			dateLimit: this.getOneWeekAgo(),
			type: TypeDocument.SPECIFIQUE
		},
		{
			id: 2,
			iconClass: 'fa-regular fa-newspaper fa-2x',
			iconTitle: 'Document PA',
			iconText: null,
			sectionTitle: 'Document PA',
			dateLimit: this.getTwoMonthAgo(),
			type: TypeDocument.PA
		},
	];
	typeClassesPdp!: string[];
	typesLibelles!: string[];
	typesHabilitations!: DisplayedTypeHab[];
	relevantPdpSignesByType: PlanPreventionTLR[][] = [];
	intervenantPaInfo!: IntervenantPaInfo;
	
	intervenant!: IntervenantTLR;
	entreprise!: EntrepriseTLR | null;
	
	displayableTypes!: DisplayedTypePdp[];

	typeDocuments: typeof TypeDocument = TypeDocument;

	constructor(
		@Inject(ParcService) parcService: ParcService,
		@Inject(IntervenantService) intervenantService: IntervenantService,
		@Inject(EntrepriseService) entrepriseService: EntrepriseService,
		@Inject(PlanPreventionService) planPreventionService: PlanPreventionService,
		@Inject(TypeHabilitationService) typeHabilitationService: TypeHabilitationService,
		@Inject(TypeQualificationService) typeQualificationService: TypeQualificationService,
		@Inject(JSR310ConversionService) dateConversionService: JSR310ConversionService,
		@Inject(DocumentPaService) documentPaService: DocumentPaService,
	) {
		// Recuperer des le chargement la map des parcs
		let parcsById$ = parcService.getParcTLRMapById();		
		combineLatest([parcsById$, this.parcId$]).subscribe(([parcsById, parcId]) => {
		  const parc = parcId ? parcsById.get(parcId) : null;
		  this.isSolaire = parc?.energie === 'Solaire';
		});
		
		// Recuperer des le chargements la liste des types de qualification (sous forme de map aussi)
		let typesQualifications$ = typeQualificationService.getList();
		
		// A la reception de l'id d'invernant, charger les types d'habilitation qui lui sont lie
		let typesHabilitations$ = this.intervenantId$.pipe(
			mergeMap(
				(idIntervenant) => typeHabilitationService.getWithQualCodesByIntervenant(idIntervenant, true)
			)
		);
		
		// Classer les types d'habilitation par type de qualification
		let typesHabilitationsByTypeQual$ = typesHabilitations$.pipe(
			map(
				(typesHabilitations) => {
					let map = new Map<number, TypeHabilitationWithQualCodesDTO[]>();
					for (let typeHabilitation of typesHabilitations) {
						let currentListForType = map.get(typeHabilitation.qualificationType);
						if (currentListForType) {
							currentListForType.push(typeHabilitation);
						} else {
							map.set(typeHabilitation.qualificationType, [typeHabilitation]);
						}
					}
					
					return map;
				}
			)
		);

		// Charger les donnees de l'intervenant		
		let intervenant$ = this.intervenantId$.pipe(
			mergeMap(
				(idIntervenant) => intervenantService.getIntervenantTLRById(idIntervenant)
			)
		);
		let entreprise$ = intervenant$.pipe(
			mergeMap(
				(intervenant) => intervenant.entrepriseId === null ?
					of(null)
					:
					entrepriseService.getActiveEntrepriseTLRById(intervenant.entrepriseId)
			)
		);
		
		// Calcul de dates servant aux evaluations ci-dessous
		let date = new Date();
		let now = date.getTime();
	 	date.setMonth(date.getMonth() + 2);
	 	let twoMonthsLater = date.getTime();
	 	
	 	// Fabrication d'une fonction utilitaire pour l'evaluation de l'expiration d'un lot d'habilitations
	 	let getExpirationStatus: (habilitations: TypeHabilitationWithQualCodesDTO[]) => ExpirationStatus = (habilitations) => {
			// Extraire les dates de fin
			let localDatesFins = (
					habilitations
					.filter($ => $.actif)
					.map($ => $.dateFin)
					.filter($ => $ != null)
				) as LocalDate[] // Ajout d'un cast parce que le compilateur ne comprend pas bien le resultat du filter
			;
			
			// Les convertir au format JS, puis en timestamp
			let timestampsFins = localDatesFins.map($ => dateConversionService.fromMandatoryLocalDate($).getTime());
			
			// Ne conserver ensuite que celles qui ne sont pas encore atteintes
			let timestampsFinsNonExpires = timestampsFins.filter($ => now <= $);
			
			if (timestampsFinsNonExpires.length === 0) {
				// S'il n'existe aucune date de fin non-expiree, le status est "expire"
				return ExpirationStatus.EXPIRED;
			}

			// Sinon, prendrre la premiere date de fin trouvee
			let timestampFin = timestampsFinsNonExpires[0];
			
			// Puis verifier si elle sera expiree dans deux mois
			if (twoMonthsLater > timestampFin) {
				return ExpirationStatus.EXPIRES_WITHIN_TWO_MONTHS;
			} else  {
				return ExpirationStatus.VALID;
			}
		};
		
		// Evaluation des couleurs des indicateurs lies a un type de qualification
		let typesClassesQual$ = typesHabilitationsByTypeQual$.pipe(
			map(
				(typesHabilitationsByTypeQual) => {

					// Evaluation de la couleur de chaque indicateur:
					// Pour chaque type de qualification faisant l'objet d'un indicateur
					let typesClasses = [];
					for (let typeQualification of this.displayedTypesQualifications) {
						let typeQualId = typeQualification.id;
						let typesHabilitationsForQual = typesHabilitationsByTypeQual.get(typeQualId);
						
						// Si l'intervenant a des habilitations pour ce type de qualification 
						if (typesHabilitationsForQual) {
							// Evaluer l'etat d'expiration de ces habilitations
							let status = getExpirationStatus(typesHabilitationsForQual);
							
							if (status === ExpirationStatus.EXPIRED) {
								// S'il n'existe aucune date de fin non-expiree, mettre l'icone en rouge
								typesClasses[typeQualId] = 'text-danger';
							} else if (status === ExpirationStatus.EXPIRES_WITHIN_TWO_MONTHS) {
								// Sinon, si les habilitations expirent dans deux mois, mettre l'icone en orange
								typesClasses[typeQualId] =  'habilitation-expiring';
							} else  {
								// Sinon, la mettre en vert
								typesClasses[typeQualId] =  'text-success';
							}
						} else {
							// Il n'y a aucune habilitation pour le type de qualification demande
							typesClasses[typeQualId] = 'text-disable';
						}
					}
					
					return typesClasses;
				}
			)
		);
		typesClassesQual$.subscribe(
			(typesClasses) => this.typesClassesQual = typesClasses
		);
		
		let parcId$ = this.parcId$.pipe(
			startWith(undefined)
		);
		
		combineLatest([this.intervenantId$, this.parcId$, this.pdpIconsToShow$]).pipe(
			mergeMap(
				([intervenantId, parcId, pdpIconsToShow]) => {
					this.displayableTypes = [];
					this.displayedTypesPdps.forEach( dtp => {
						if (pdpIconsToShow.includes(dtp.type)) {
							this.displayableTypes.push(dtp);
						}
					} );
					if (this.displayableTypes.length) {
						return documentPaService.intervenantPaInfo(intervenantId, parcId, this.getTwoMonthAgo());
					} else {
						return of({} as IntervenantPaInfo);
					}
				}
			)
		).subscribe(
			(intervenantPaInfo) => {
				console.log('intervenantPaInfo: ', intervenantPaInfo);
				this.intervenantPaInfo = intervenantPaInfo;
				if (this.typeClassesPdp && intervenantPaInfo.entreprisePa) {
					if (intervenantPaInfo.paInfos && intervenantPaInfo.paInfos.length) {
						this.typeClassesPdp[2] = 'text-success';
					}
				}
			}
		);
		
			
		// Evaluation des habilitations a afficher dans le resume des habilitations de l'intervenant
		let typesHabilitationsToDisplay$ = combineLatest([parcsById$, typesQualifications$, typesHabilitationsByTypeQual$, parcId$]).pipe(
			map(
				(data) => {
					let parcsById = new MandatoryMap<number, ParcTLR>(data[0], "Parc");
					let typesQualifications = data[1];
					let typesHabilitationsByTypeQual = data[2];
					let parcId = data[3];
					
					// Construction du resume des habilitations de l'intervenant:
					// Pour chaque type de qualification connu
					let typesHabilitationsToDisplay: DisplayedTypeHab[] = [];
					for (let typeQualification of typesQualifications) {
						let typeQualId = typeQualification.id;
						let typesHabilitationsForQual = typesHabilitationsByTypeQual.get(typeQualId);
						
						// Si l'intervenant a des habilitations pour cette qualification
						if (typesHabilitationsForQual) {
							let status = getExpirationStatus(typesHabilitationsForQual);

							// Preparer l'affichage de l'habilitation
							let typesHabilitationsA1EtDetachement = typesHabilitationsForQual.filter( $ => $.qualificationType === 15 );
							let typesHabilitationsAutres = typesHabilitationsForQual.filter( $ => $.qualificationType !== 15 );
							typesHabilitationsAutres.forEach( $ => {
								let displayedTypeHab: DisplayedTypeHab = {
									nomType: typeQualification.nom,
									codeParc: $.parc === null ? null : parcsById.get($.parc).code,
									qualificationCodes: $.qualificationCodes.join(", "),
									actif: $.actif,
									dateFin: dateConversionService.fromNullableLocalDate($.dateFin),
									valid: status === ExpirationStatus.VALID,
									qualificationTypeId: typeQualId,
								};
								typesHabilitationsToDisplay.push(displayedTypeHab);
							} );
							typesHabilitationsA1EtDetachement.forEach( $ => {
								if (parcId === $.parc) {
									let displayedTypeHab: DisplayedTypeHab = {
										nomType: typeQualification.nom,
										codeParc: $.parc === null ? null : parcsById.get($.parc).code,
										qualificationCodes: $.qualificationCodes.join(", "),
										actif: $.actif,
										dateFin: dateConversionService.fromNullableLocalDate($.dateFin),
										valid: status === ExpirationStatus.VALID,
										qualificationTypeId: typeQualId,
									};
									typesHabilitationsToDisplay.push(displayedTypeHab);
								}
							} );
						}
					}
					
					return typesHabilitationsToDisplay;
				}
			)
		);
		typesHabilitationsToDisplay$.subscribe(
			(typesHabilitations) => this.typesHabilitations = typesHabilitations
		);

		let signedPdpByOuvrage$ = combineLatest([this.extended$, this.intervenantId$, this.ouvragesIds$]).pipe(
			mergeMap(
				([extended, intervenantId, ouvragesIds]) => {
					// Si on affiche pas les informations extendue ou si il n'y a pas d'ouvrage specifie
					// ne pas rechercher de pdp en base
					if (!extended || !ouvragesIds) {
						return of([] as OuvrageAndPdPs[]);
					}
					
					// Evaluer la date limite la plus ancienne parmis tous les types de pdp a afficher
					let olderDateLimit = this.displayedTypesPdps
						.map($ => $.dateLimit)
						.reduce(
							($1, $2) => {
								if ($1.getTime() > $2.getTime()) {
									return $2;
								}
								return $1;
							},
							this.now
						)
					;
					
					// Charger les informations des PdP
					let signedPdpByOuvrage$ = ouvragesIds.map(
						ouvrageId => planPreventionService.listSignedPdp(intervenantId, ouvrageId, olderDateLimit).pipe(
							map(
								(pdps) => ({
									ouvrageId: ouvrageId,
									pdps: pdps,
								} as OuvrageAndPdPs)
							)
						)
					);
					return safeForkJoin(signedPdpByOuvrage$);
				}
			)
		);
		
		// Classe les PDPs par leur type, et ne garde que ceux relevants selon la date limite de leur type
		let relevantSignedPdPsByTypeAndOuvrage$ = signedPdpByOuvrage$.pipe(
			map(
				(signedPdpByOuvrage) => {
					let pdpByType: OuvrageAndPdPs[][] = [];
					this.displayedTypesPdps.forEach(displayedType => {
						let typeId = displayedType.id;
						let dateLimit = displayedType.dateLimit;
						let localDateLimit = dateConversionService.toNullableLocalDate(dateLimit);
						
						let filteredPdPByOuvrage = signedPdpByOuvrage.map($ => {
							let ouvrageId = $.ouvrageId;
							let signedPdPs = $.pdps;
							
							let signedPdPsForTypeAndOuvrage = signedPdPs.filter(signedPdP => signedPdP.typePdp === typeId);
	
							// Filtrer selon la date limite
							let filteredPdps = signedPdPsForTypeAndOuvrage.filter(pdp => {
								let finValidite = pdp.finValidite;
								let compare = dateConversionService.compareLocalDate(finValidite, localDateLimit, true);
								return compare >= 0;
							});
							
							return {
								ouvrageId: ouvrageId,
								pdps: filteredPdps,
							} as OuvrageAndPdPs;
						})
						
						pdpByType[typeId] = filteredPdPByOuvrage;
					});
					
					return pdpByType;
				}
			)
		);
		
		// Rassemble les PDP par type, en ignorant cette fois leurs ouvrages lies, enlevenant les doublons et les classant par date d'expiraion
		let relevantPdpSignesByType$ = relevantSignedPdPsByTypeAndOuvrage$.pipe(
			map(
				(filteredSignedPdPsByTypeAndOuvrage) => {
					let relevantPdpSignesByType: PlanPreventionTLR[][] = [];
					this.displayedTypesPdps.forEach(displayedType => {
						let typeId = displayedType.id;
						let pdpForOuvrages = filteredSignedPdPsByTypeAndOuvrage[typeId];
						
						// Cumuler dans une listes les PdP independamment de leurs ouvrages
						let pdps: PlanPreventionTLR[] = [];
						pdpForOuvrages.forEach($ => {
							let pdpsForOuvrage = $.pdps;
							pdps.push(...pdpsForOuvrage);
						})
						
						// Retirer les doubles
						let uniquePdps = filterUniques(pdps);
						
						// Trier par date
						let sortedPdps = uniquePdps.sort((pdp1, pdp2) => dateConversionService.compareLocalDate(pdp1.finValidite, pdp2.finValidite, true));
						
						relevantPdpSignesByType[typeId] = sortedPdps;
					});
					return relevantPdpSignesByType;
				}
			)
		)
		
		// Bind les PDPs obtenus ci-dessus sur la vue
		relevantPdpSignesByType$.subscribe(
			(relevantPdpSignesByType) => this.relevantPdpSignesByType = relevantPdpSignesByType
		);
		
		// Calcul du statut de l'intervenant pour chaque type de pdp a afficher
		let intervenantStatusByPdPType$ = combineLatest([relevantSignedPdPsByTypeAndOuvrage$]).pipe(
			map(
				([filteredSignedPdPsByTypeAndOuvrage]) => {
					let intervenantStatusByPdPType: (boolean | null)[] = [];
					this.displayedTypesPdps.forEach(displayedType => {
						let typeId = displayedType.id;
						
						let status: boolean | null = null;
						let today = dateConversionService.toNullableLocalDate(this.now);
						let isValid = (pdp: PlanPreventionTLR) => dateConversionService.compareLocalDate(pdp.finValidite, today, true) >= 0;
						let filteredSignedPdPsByOuvrage = filteredSignedPdPsByTypeAndOuvrage[typeId];
						
						// Rester sur le status 'null' s'il n'y a pas d'ouvrage
						if (filteredSignedPdPsByOuvrage.length > 0) {
							// Le status est true si on ne trouve aucun ouvrage sans PdP signe qui soit valide
							status = filteredSignedPdPsByOuvrage.findIndex(ouvrageWithPdPs => {
								let signedPdPs = ouvrageWithPdPs.pdps;
								let noPdPFound = signedPdPs.findIndex(signedPdp => isValid(signedPdp)) < 0;
								return noPdPFound;
							}) < 0;
						}
						
						intervenantStatusByPdPType[typeId] = status;
					});
					
					return intervenantStatusByPdPType;
				}
			)
		);
			
		// Transformer les status de signature pour chaque type de pdp en classe
		let typeClassesPdp$ = intervenantStatusByPdPType$.pipe(
			map(
				(pdpTypesStatus) => {
					let typeClassesPdp = [] as string[];
					
					this.displayedTypesPdps.forEach($ => {
						let typeId = $.id;
						let status = pdpTypesStatus[typeId];
						
						let cssClass = 'text-danger';
						if (status === true) {
							cssClass = 'text-success';
						}
						
						typeClassesPdp[typeId] = cssClass;
					});
					
					return typeClassesPdp;
				}
			)
		);
		
		// Bind les classes de style pour les types de pdp
		typeClassesPdp$.subscribe(
			(typeClassesPdp) => {
				if (this.intervenantPaInfo && this.intervenantPaInfo.paInfos && this.intervenantPaInfo.paInfos.length) {
					typeClassesPdp[2] = 'text-success';
				}
				this.typeClassesPdp = typeClassesPdp;
			}
		);

		////////////////////
		// Fin evol 59641 //
		////////////////////
		
		// La premiere fois qu'on a l'integralite des bindings, proceder a l'affichage
		combineLatest([typesClassesQual$, typesHabilitationsToDisplay$, intervenant$, entreprise$, typeClassesPdp$]).subscribe(
			([typesClassesQual, typesHabilitationsToDisplay, intervenant, entreprise, _typeClassesPdp]) => {
				if (!this.loaded) {
					// S'assurer que les bindings sont faits, au cas ou les subscribe au dessus ne se soient pas tous executes avant celui-ci
					this.typesClassesQual = typesClassesQual;
					this.typesHabilitations = typesHabilitationsToDisplay;
					this.intervenant = intervenant;
					this.entreprise = entreprise;
					
					// NB: les types de classe PDP sont bind a part car elles changent en fonction des ouvrages
					// et les ouvrages peuvent changer post chargement du composant
					
					// Quand toutes les informations sont calculees, on permet l'affichage
					this.loaded = true;
				}	
			}
		);
	}
	
	@Input() extended = true;

	@Input() set idIntervenant(value: number) {
		this.intervenantId$.next(value);
	}
	
	@Input() set shouldBlink(value: string) {
	  this.shouldBlink$.next(value); 
	}
	
	@Input() set pdpIconsToShow(value: TypeDocument[]) {
	  this.pdpIconsToShow$.next(value); 
	}
	
	@Input() set idOuvrages(value: number[] | null | undefined) {
		if (value === null || value === undefined) {
			this.ouvragesIds$.next([] as number[]);
		} else {
			this.ouvragesIds$.next(value);
		}
	}
	
	@Input() set ouvrages(value: OuvrageTLR[] | null | undefined) {
		if (value === null || value === undefined) {
			this.ouvragesIds$.next([] as number[]);
		} else {
			this.ouvragesIds$.next(value.map($ => $.id));
		}
	}

	@Input() set idParc(value: number | null | undefined) {
		if (value === undefined) {
			this.parcId$.next(null);
		} else {
			this.parcId$.next(value);
		}
	}
	
	@Input() spacing: string | null = null;
}